// =============================================================================
// FILE: packages/renderer/src/stores/capture.store.ts
// PURPOSE: R2 Capture Mode state machine. Collect → Review → (User Approval) →
//   Ingest → QA/QC. Nothing is permanently ingested until the user approves.
//   Silent metadata (source, date, type, keywords) is attached to each item.
// =============================================================================

import { create } from 'zustand';
import { analyzeCapture, extractKeywords, type QAReport } from '../lib/capture/qaqc.js';

export type CaptureType = 'text' | 'link' | 'email' | 'note' | 'document' | 'image' | 'screenshot' | 'attachment';
export type CaptureStage = 'idle' | 'config' | 'collecting' | 'review' | 'ingested';

export interface CaptureItem {
  id: string; type: CaptureType; content: string; source: string;
  author?: string; date: string; keywords: string[];
}

export const CAPTURE_TYPES: { id: CaptureType; label: string }[] = [
  { id: 'text', label: 'Text' }, { id: 'link', label: 'Web Links' }, { id: 'email', label: 'Emails' },
  { id: 'note', label: 'Notes' }, { id: 'document', label: 'Documents' }, { id: 'image', label: 'Images' },
  { id: 'screenshot', label: 'Screenshots' }, { id: 'attachment', label: 'Attachments' },
];

interface CaptureState {
  open: boolean;
  stage: CaptureStage;
  enabled: Record<CaptureType, boolean>;
  items: CaptureItem[];
  report: QAReport | null;
  openPanel: () => void;
  close: () => void;
  toggleType: (t: CaptureType) => void;
  startCollecting: () => void;
  addItem: (type: CaptureType, content: string, source?: string, author?: string) => void;
  removeItem: (id: string) => void;
  toReview: () => void;
  backToCollecting: () => void;
  approve: () => void;
  reset: () => void;
}

const allOn = Object.fromEntries(CAPTURE_TYPES.map((t) => [t.id, true])) as Record<CaptureType, boolean>;

export const useCapture = create<CaptureState>((set, get) => ({
  open: false,
  stage: 'config',
  enabled: { ...allOn },
  items: [],
  report: null,

  openPanel: () => set({ open: true, stage: get().items.length ? get().stage : 'config' }),
  close: () => set({ open: false }),
  toggleType: (t) => set((s) => ({ enabled: { ...s.enabled, [t]: !s.enabled[t] } })),
  startCollecting: () => set({ stage: 'collecting' }),

  addItem: (type, content, source = 'Pasted', author) => {
    const item: CaptureItem = {
      id: crypto.randomUUID(), type, content: content.trim(), source,
      author, date: new Date().toISOString(), keywords: extractKeywords(content),
    };
    set((s) => ({ items: [item, ...s.items] }));
  },
  removeItem: (id) => set((s) => ({ items: s.items.filter((i) => i.id !== id) })),

  toReview: () => set({ stage: 'review' }),
  backToCollecting: () => set({ stage: 'collecting' }),

  approve: () => set({ stage: 'ingested', report: analyzeCapture(get().items) }),

  reset: () => set({ stage: 'config', items: [], report: null, enabled: { ...allOn } }),
}));
